import React, { ReactElement, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import {
  Loading,
  alertError,
  alertSuccess,
  Button,
  ButtonStyle,
  InputGroup,
  ISelectOption,
  enumToOptions,
  SelectField,
  stringsToOptions,
  ZIndex,
  stringToOption,
  IGiftItemResponse,
  GiftItemStatus,
  GiftItemType,
  AsyncButton,
  IPromotionResponse,
} from '@one-tree/library'
import download from 'downloadjs'
import CardHeader from '../../components/page/CardHeader'
import { bulkPatchGiftItems, getGiftItems } from '../../helpers/APIItemHelper'
import { RoutePath } from '../../types/Routes'
import GiftItemGrid from './giftItemGrid/GiftItemGrid'
import {
  filterGiftItems,
  getView,
  typeHandler,
  View,
} from '../../helpers/GiftItemHelper'
import { useOrganisation } from '../../context/OrganisationProvider'
import { stripEmptyFromArray } from '../../helpers/DataTransformer'
import CardContent from '../../components/page/CardContent'
import { OrganisationFormat } from '../../types/Types'
import { IGiftItemFilters } from '../../types/GiftItemTypes'
import { itemWord } from '../../helpers/FormatHelper'
import {
  ExportStatus,
  exportTickets,
  exportOffers,
  get,
} from '../../helpers/APIHelper'
import { ICategoryResponse, Resource } from '../../types/API'
import { getCategoriesAsOptions } from '../../helpers/CategoryHelper'
import { isGiftItemValid } from '../../helpers/GiftItemValidityHelper'
import GiftItemTable from './giftItemTable/GiftItemTable'
import {
  convertPromoResponseToPromotion,
  convertPromotionToPromoResponse,
  isPromoActive,
  PromoDot,
  PromoOption,
  Promotions,
} from './promotion/PromotionHelper'

const StyledInputGroup = styled(InputGroup)<{ isTicketTable: boolean }>`
  /* so the controls are above the ScrollZone */
  z-index: ${ZIndex.AboveContent};

  flex-wrap: wrap;
  row-gap: 10px;

  /* Put Download CSV button on right */
  & > *:last-child {
    ${({ isTicketTable }): string => (isTicketTable ? 'margin-left: auto;' : '')}
  }
`
const PublishButtons = styled.div`
  display: flex;
  column-gap: 10px;
`
const Controls = styled.div`
  display: flex;
`
const Message = styled.div`
  padding-top: 20px;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`
function ManageGiftItems({ type }: { type: 'grid' | 'table' }): ReactElement {
  const [giftItemData, setGiftItemData] = useState<IGiftItemResponse[]>([])
  const [categories, setCategories] = useState<ISelectOption<number>[]>()
  const [rawCategories, setRawCategories] = useState<ICategoryResponse[]>()
  const [allTypes, setAllTypes] = useState<string[]>()
  const [promotions, setPromotions] = useState<Promotions>()
  const [loading, setLoading] = useState(false)
  const history = useHistory()
  const { organisation } = useOrganisation()
  const [filteredGiftItems, setFilteredGiftItems] = useState<
    IGiftItemResponse[]
  >([])

  const initView = getView(sessionStorage.getItem('manage-view'))
  const [view, setView] = useState<View>(initView)

  const storedFilters = sessionStorage.getItem('manageFilters')
  const initFilters = storedFilters
    ? JSON.parse(storedFilters)
    : {
        status: false,
        type: false,
        category: false,
        unfinished: false,
        expired: false,
        promotion: false,
      }
  const [filters, setFilters] = useState<IGiftItemFilters>(initFilters)
  const [archived, setArchived] = useState(
    initFilters.status === GiftItemStatus.Archived,
  )

  const updateFilters = <T extends unknown>(
    key: keyof IGiftItemFilters,
    update: T,
  ): void => {
    setFilters((prevState) => {
      const newFilterState = {
        ...prevState,
        [key]: update,
      }
      sessionStorage.setItem('manageFilters', JSON.stringify(newFilterState))
      return newFilterState
    })
  }

  const fetchGiftItems = async (noLoad?: true): Promise<void> => {
    if (!noLoad) setLoading(true)
    const res = organisation
      && (await getGiftItems({
        format: organisation.format,
        archived,
      }))
    if (res) setGiftItemData(res)
    setLoading(false)
  }

  const fetchCategories = async (): Promise<void> => {
    const res = await get<ICategoryResponse[]>({
      resource: Resource.Categories,
    })
    if (res) {
      setRawCategories(res)
      setCategories(getCategoriesAsOptions(res))
    }
  }

  const fetchPromotions = async (): Promise<void> => {
    const res = await get<IPromotionResponse[]>({ resource: Resource.Promotions })
    if (res) {
      setPromotions(res.map(convertPromoResponseToPromotion))
    }
  }

  useEffect(() => {
    fetchGiftItems()
    if (organisation?.categoriesEnabled && !categories) fetchCategories()
    if (organisation?.hasPromotions && !promotions) fetchPromotions()
  }, [archived])

  useEffect(() => {
    const filteredGiftItemData = filterGiftItems(
      giftItemData,
      filters,
      organisation,
      promotions?.map(convertPromotionToPromoResponse),
      rawCategories,
    )
    setFilteredGiftItems(filteredGiftItemData)
    if (filters.type) {
      // If we are filtering by type, show all other types, so we can change it
      // to another type
      setAllTypes(typeHandler(giftItemData))
    } else {
      // otherwise show only types available based on the filtered results
      setAllTypes(typeHandler(filteredGiftItemData))
    }
  }, [filters, giftItemData, promotions])

  const handleMultiStatusChange = async (
    items: IGiftItemResponse[] | false,
    newStatus: GiftItemStatus,
  ): Promise<void> => {
    if (items) {
      const bulkPatchOptions = items
        // only patch valid items
        .filter(
          (item: IGiftItemResponse) => isGiftItemValid(
            item,
            organisation,
            undefined,
            rawCategories,
          ),
        )
        .map((item) => {
          // ignore items that already have the status we want
          if (item.status === newStatus) {
            return null
          }
          return { id: item.id, status: newStatus }
        })
      const filteredOptions = stripEmptyFromArray(bulkPatchOptions)
      if (!filteredOptions.length) {
        alertError(`No items eligible to be ${newStatus}`)
      } else {
        const res = organisation
          && (await bulkPatchGiftItems({
            format: organisation.format,
            patchOptions: filteredOptions,
          }))
        if (res) {
          alertSuccess(
            `${res.length} item${
              res.length > 1 ? 's were' : ' was'
            }  ${newStatus}`,
          )
          fetchGiftItems()
        }
      }
    }
  }

  const publishableItems = filteredGiftItems.filter(
    (item: IGiftItemResponse) => isGiftItemValid(item, organisation, undefined, rawCategories)
      && item.status === GiftItemStatus.Unpublished
      && item.type !== GiftItemType.Complimentary,
  )
  const unpublishableItems = filteredGiftItems.filter(
    (item: IGiftItemResponse) => item.status === GiftItemStatus.Published,
  )

  const isTESO = organisation?.format !== OrganisationFormat.GiftVouchers

  const unfinishedOption = {
    value: 'Unfinished',
    label: 'Unfinished',
  }
  const expiredOption = {
    value: 'Expired',
    label: 'Expired',
  }
  const filterOptions: ISelectOption<string>[] = [
    ...enumToOptions(GiftItemStatus),
    unfinishedOption,
    ...(isTESO ? [expiredOption] : []),
  ]

  const publishPlural = publishableItems.length > 1 ? 's' : ''
  const publishAllMessage = `Are you sure you want to publish ${publishableItems.length} item${publishPlural}?`

  const unpublishPlural = unpublishableItems.length > 1 ? 's' : ''
  const unpublishAllMessage = `Are you sure you want to unpublish ${unpublishableItems.length} item${unpublishPlural}?`

  const getStatusValue = (): ISelectOption<string> | undefined => {
    if (filters.unfinished) return unfinishedOption
    if (filters.expired) return expiredOption

    const statusOption = stringToOption(filters.status || '')

    // Since the actual label from backend is 'inactive'
    if (statusOption?.value === GiftItemStatus.Archived) {
      return { value: GiftItemStatus.Archived, label: 'Archived' }
    }
    return statusOption
  }

  const showingArchived = getStatusValue()?.value === GiftItemStatus.Archived
  let normalText = `Create and amend ${itemWord(
    organisation,
  )}s here. Publish and unpublish to show or hide each ${itemWord(
    organisation,
  )} on your web shop.`

  const archivedText = 'These items are archived. Click \'Restore\' to make them available again for use on your web shop.'

  const promoOptions = (promotions ?? []).map((promo) => ({
    label: (
      <PromoOption>
        <PromoDot active={isPromoActive(promo)} />
        {promo.name}
      </PromoOption>
    ),
    value: promo.id,
  }))

  const isTable = type === 'table'
  const isTicketTable = isTable && organisation?.format === OrganisationFormat.Tickets
  const isOfferTable = isTable && organisation?.format === OrganisationFormat.SpecialOffers

  if (isTicketTable) {
    normalText = 'Overview of ticket availability and sales. Actions taken here will impact the display of the tickets on your webpage.'
  }

  const hasPublish = publishableItems?.length > 0
  const hasUnpublish = unpublishableItems?.length > 0

  const handleDownloadTickets = async (): Promise<void> => {
    let exportStatus: ExportStatus = filters.status

    if (filters.unfinished) exportStatus = 'unfinished'
    if (filters.expired) exportStatus = 'expired'

    const res = await exportTickets({
      status: exportStatus,
      category: filters.category,
    })

    if (res) {
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      download(res, `${date}_ticket_export.csv`)
    }
  }

  const handleDownloadOffers = async (): Promise<void> => {
    let exportStatus: ExportStatus = filters.status

    if (filters.unfinished) exportStatus = 'unfinished'
    if (filters.expired) exportStatus = 'expired'

    const res = await exportOffers({
      status: exportStatus,
      category: filters.category,
    })

    if (res) {
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      download(res, `${date}_special_offer_export.csv`)
    }
  }

  const content = type === 'table' ? (
    <GiftItemTable
      items={filteredGiftItems}
      setItems={setGiftItemData}
      archived={showingArchived}
      promotions={promotions}
      categories={rawCategories}
      onPromoUpdate={(updated): void => {
          setPromotions((p) => p?.map((each) => (each.id === updated.id ? updated : each)))
        }}
    />
    ) : (
      <GiftItemGrid
        giftItemData={filteredGiftItems}
        setGiftItemData={setGiftItemData}
        showControls={true}
        compact={view === View.Compact}
        categories={rawCategories}
      />
    )

  return (
    <>
      <CardHeader title={`Manage ${itemWord(organisation)}s`}>
        <Controls>
          {type === 'grid' && (
            <SelectField
              isSearchable={false}
              placeholder="Select view..."
              options={[
                { label: `${View.Full} view`, value: View.Full },
                { label: `${View.Compact} view`, value: View.Compact },
              ]}
              value={{ label: `${view} view`, value: view }}
              onChange={(option): void => {
                if (option) {
                  setView(option.value)
                  sessionStorage.setItem('manage-view', option.value)
                }
              }}
            />
          )}
          <Button
            buttonStyle={ButtonStyle.Action}
            onClick={(): void => history.push(RoutePath.CreateItem)}
          >
            Create new
          </Button>
        </Controls>
      </CardHeader>
      <CardContent>
        {!isTable && <p>{showingArchived ? archivedText : normalText}</p>}
        {!isTable && !showingArchived && !isTicketTable && (
          <p>
            {`Amendments made here will appear on your website. 'Full view' is similar to how your web shop is displayed. Compact view is available in the management portal only for a succinct overview of all ${itemWord(organisation)} items.`}
          </p>
        )}
        <StyledInputGroup isTicketTable={isTicketTable}>
          <SelectField
            placeholder="Filter by status..."
            options={filterOptions}
            value={getStatusValue()}
            onChange={(option): void => {
              setArchived(option?.value === GiftItemStatus.Archived)

              if (option) {
                if (option.value === unfinishedOption.value) {
                  updateFilters('status', false)
                  updateFilters('unfinished', true)
                  updateFilters('expired', false)
                } else if (option.value === expiredOption.value) {
                  updateFilters('status', false)
                  updateFilters('unfinished', false)
                  updateFilters('expired', true)
                } else {
                  // if we remove unfinishedOption, the remainder is GiftItemStatus
                  // eslint-disable-next-line no-type-assertion/no-type-assertion
                  updateFilters('status', option.value as GiftItemStatus)
                  updateFilters('unfinished', false)
                  updateFilters('expired', false)
                  if (option.value === GiftItemStatus.Archived) {
                    updateFilters('promotion', false)
                  }
                }
              } else {
                updateFilters('status', false)
                updateFilters('unfinished', false)
                updateFilters('expired', false)
              }
            }}
            isClearable={true}
          />
          {organisation?.format === OrganisationFormat.GiftVouchers && (
            <SelectField
              placeholder="Filter by type..."
              options={stringsToOptions(allTypes || [])}
              value={stringToOption(filters.type || '')}
              onChange={(option): void => {
                if (option) {
                  updateFilters('type', option.value)
                } else {
                  updateFilters('type', false)
                }
              }}
              isClearable={true}
              disabled={!allTypes}
            />
          )}
          {organisation?.categoriesEnabled && (
            <SelectField
              placeholder="Filter by category..."
              options={categories || []}
              value={
                categories
                && categories.find(
                  (category) => category.value === filters.category,
                )
              }
              onChange={(option): void => {
                if (option) {
                  updateFilters('category', option.value)
                } else {
                  updateFilters('category', false)
                }
              }}
              isClearable={true}
              disabled={!categories}
            />
          )}
          {!showingArchived && !isTESO && organisation?.hasPromotions && (
            <SelectField
              placeholder={
                promotions && !promotions.length
                  ? 'No promotions found'
                  : 'Filter by promotion...'
              }
              options={promoOptions}
              value={
                filters.promotion
                  ? promoOptions.find(
                      ({ value }) => value === filters.promotion,
                    )
                  : undefined
              }
              onChange={(option): void => {
                updateFilters('promotion', option ? option.value : false)
              }}
              isClearable={true}
              disabled={!promotions?.length}
            />
          )}
          {(hasPublish || hasUnpublish) && !isTable && (
            <PublishButtons>
              {hasPublish && (
                <Button
                  buttonStyle={ButtonStyle.Action}
                  onClick={(): Promise<void> => handleMultiStatusChange(
                      filteredGiftItems,
                      GiftItemStatus.Published,
                    )}
                  showConfirmation={true}
                  confirmationMessage={publishAllMessage}
                >
                  Publish all
                </Button>
              )}
              {hasUnpublish && (
                <Button
                  buttonStyle={ButtonStyle.Secondary}
                  onClick={(): Promise<void> => handleMultiStatusChange(
                      filteredGiftItems,
                      GiftItemStatus.Unpublished,
                    )}
                  showConfirmation={true}
                  confirmationMessage={unpublishAllMessage}
                >
                  Unpublish all
                </Button>
              )}
            </PublishButtons>
          )}
          {isTicketTable && (
            <AsyncButton onClick={handleDownloadTickets}>
              Download CSV
            </AsyncButton>
          )}
          {isOfferTable && (
            <AsyncButton onClick={handleDownloadOffers}>
              Download CSV
            </AsyncButton>
          )}
        </StyledInputGroup>
        {loading ? <Loading fullPage={true} /> : content}
        {!loading && filteredGiftItems.length === 0 && (
          <Message>No items</Message>
        )}
      </CardContent>
    </>
  )
}
export default ManageGiftItems
